import { useState, useEffect, useCallback } from "react";
import {
    Loader2, X, ShoppingCart, Handshake, Check, Ban, Send, Undo2,
    Phone, Mail, User, Hash, CheckCircle2, Euro,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { CustomerDataModal } from "../listing/dispo/CustomerDataModal";
import { ContractSignModal } from "../listing/dispo/ContractSignModal";

const STATUS = {
    open: { label: "Offen", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    accepted: { label: "Angenommen", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    rejected: { label: "Abgelehnt", cls: "bg-red-100 text-red-700 border-red-300" },
    withdrawn: { label: "Zurückgezogen", cls: "bg-gray-100 text-gray-600 border-gray-300" },
    completed: { label: "Abgeschlossen", cls: "bg-[#111827] text-[#FFCC00] border-[#111827]" },
};
const REJECT_OPTS = [
    { code: "inseratsfehler", label: "Inseratsfehler" },
    { code: "preisfehler", label: "Preisfehler" },
    { code: "sonstiges", label: "Sonstiges" },
];

/**
 * Jun-2026 (User-Wunsch) — Detailansicht einer B2B-Anfrage (Kauf/Vermittlung)
 * mit Kontaktdaten, echter Nachrichten-Historie und Aktionen:
 * Annehmen (Kauf: Schritt-für-Schritt | vollautomatisch), Ablehnen (Grund),
 * Provisions-Gegenvorschlag (Vermittlung, einmalig), Nachricht, Zurückziehen.
 */
export default function B2BDealDetailModal({ dealId, accountId, onClose, onChanged }) {
    const [deal, setDeal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [showReject, setShowReject] = useState(false);
    const [rejectCode, setRejectCode] = useState("inseratsfehler");
    const [rejectText, setRejectText] = useState("");
    const [autoMode, setAutoMode] = useState(false);
    const [provision, setProvision] = useState("");
    const [showProvision, setShowProvision] = useState(false);
    // Jul-2026 (User-Wunsch) — Vermittler-Checkout im Namen des Besitzers
    const [brokerStep, setBrokerStep] = useState(null); // "customer" | "contract"

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/dealer/b2b/deals/${dealId}`);
            setDeal(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Anfrage konnte nicht geladen werden");
            onClose?.();
        } finally { setLoading(false); }
    }, [dealId, onClose]);

    useEffect(() => { load(); }, [load]);

    const role = deal ? (deal.to_dealer_id === accountId ? "seller" : (deal.from_dealer_id === accountId ? "buyer" : "viewer")) : null;
    const canManage = deal?.can_manage && role === "seller";
    const isOpen = deal?.status === "open";

    const act = async (fn, okMsg) => {
        setBusy(true);
        try { await fn(); if (okMsg) toast.success(okMsg); await load(); onChanged?.(); }
        catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
        finally { setBusy(false); }
    };

    const sendMessage = () => {
        if (!msg.trim()) return;
        act(async () => { await api.post(`/dealer/b2b/deals/${dealId}/message`, { text: msg.trim() }); setMsg(""); });
    };
    const accept = () => act(
        async () => api.post(`/dealer/b2b/deals/${dealId}/accept`, { auto_mode: autoMode }),
        deal.mode === "kauf" ? "Kaufanfrage angenommen · Verkaufsprozess gestartet" : "Vermittlung freigegeben");
    const reject = () => act(
        async () => { await api.post(`/dealer/b2b/deals/${dealId}/reject`, { reason_code: rejectCode, reason_text: rejectText.trim() || undefined }); setShowReject(false); },
        "Anfrage abgelehnt · Admin informiert");
    const withdraw = () => act(async () => api.post(`/dealer/b2b/deals/${dealId}/withdraw`), "Anfrage zurückgezogen");
    const complete = () => act(async () => api.post(`/dealer/b2b/deals/${dealId}/complete`), "Als abgeschlossen markiert");
    const sendProvision = () => {
        const v = parseInt(provision, 10);
        if (!Number.isFinite(v) || v < 0) { toast.error("Bitte gültigen Betrag eingeben"); return; }
        act(async () => { await api.post(`/dealer/b2b/deals/${dealId}/provision-counter`, { provision_eur: v }); setShowProvision(false); }, "Gegenvorschlag gesendet");
    };

    const st = deal && STATUS[deal.status];
    const contact = deal?.from_dealer_contact || {};

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="b2b-deal-detail-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {deal?.mode === "vermittlung" ? <Handshake className="w-5 h-5 text-[#FFCC00]" /> : <ShoppingCart className="w-5 h-5 text-[#FFCC00]" />}
                        {deal?.mode === "vermittlung" ? "Vermittlungsanfrage" : "Kaufanfrage"}
                        {st && <span className={`ml-2 text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${st.cls}`} data-testid="b2b-deal-status">{st.label}</span>}
                    </DialogTitle>
                    <DialogDescription className="sr-only">B2B-Anfrage – Kontaktdaten, Nachrichten und Aktionen</DialogDescription>
                </DialogHeader>

                {loading || !deal ? (
                    <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                ) : (
                    <div className="space-y-4">
                        {/* Fahrzeug + Preis */}
                        <div className="bg-gray-50 border border-gray-200 rounded-sm p-3">
                            <div className="font-bold text-[#111827]" data-testid="b2b-deal-vehicle">{deal.vehicle_label}</div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
                                {deal.price_b2b != null && <span>Händlerpreis: <b>{Number(deal.price_b2b).toLocaleString("de-DE")} €</b></span>}
                                {deal.mode === "vermittlung" && (
                                    <span>Provision: <b>{(deal.provision_counter_eur ?? deal.provision_eur ?? 0).toLocaleString("de-DE")} €</b>{deal.provision_counter_eur != null && <span className="text-amber-700"> (Gegenvorschlag)</span>}</span>
                                )}
                            </div>
                        </div>

                        {/* Kontaktdaten des anfragenden Händlers */}
                        <div className="border border-gray-200 rounded-sm p-3" data-testid="b2b-deal-contact">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Anfragender Händler</div>
                            <div className="text-sm font-semibold text-[#111827]">{contact.company || deal.from_dealer_name}</div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
                                {contact.contact_name && <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5" />{contact.contact_name}</span>}
                                {contact.phone && <a href={`tel:${contact.phone}`} className="inline-flex items-center gap-1 text-blue-700 hover:underline" data-testid="b2b-contact-phone"><Phone className="w-3.5 h-3.5" />{contact.phone}</a>}
                                {contact.email && <a href={`mailto:${contact.email}`} className="inline-flex items-center gap-1 text-blue-700 hover:underline" data-testid="b2b-contact-email"><Mail className="w-3.5 h-3.5" />{contact.email}</a>}
                            </div>
                            {(deal.from_transaction_code || deal.to_transaction_code) && (
                                <div className="text-[11px] text-gray-400 mt-1.5 inline-flex items-center gap-1"><Hash className="w-3 h-3" />Transaktion: {deal.from_transaction_code || "–"} → {deal.to_transaction_code || "–"}</div>
                            )}
                        </div>

                        {/* Endkunde bei Vermittlung */}
                        {deal.mode === "vermittlung" && deal.customer && (
                            <div className="border border-gray-200 rounded-sm p-3 bg-amber-50/40">
                                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Endkunde (Vermittlung)</div>
                                <div className="text-sm text-gray-700">
                                    {[deal.customer.first_name, deal.customer.last_name].filter(Boolean).join(" ")} {deal.customer.company_name ? `· ${deal.customer.company_name}` : ""}
                                    {deal.customer.email ? ` · ${deal.customer.email}` : ""}{deal.customer.phone ? ` · ${deal.customer.phone}` : ""}
                                </div>
                            </div>
                        )}

                        {/* Ablehnungsgrund */}
                        {deal.status === "rejected" && (
                            <div className="border border-red-200 bg-red-50 rounded-sm p-3 text-sm text-red-800" data-testid="b2b-deal-reject-reason">
                                <b>Abgelehnt:</b> {REJECT_OPTS.find((r) => r.code === deal.reject_reason_code)?.label || deal.reject_reason_code}
                                {deal.reject_reason_text ? ` — ${deal.reject_reason_text}` : ""}
                            </div>
                        )}

                        {/* Nachrichten-Historie */}
                        <div>
                            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Nachrichten</div>
                            <div className="space-y-2 max-h-52 overflow-y-auto pr-1" data-testid="b2b-deal-messages">
                                {(deal.messages || []).length === 0 && <div className="text-sm text-gray-400">Noch keine Nachrichten.</div>}
                                {(deal.messages || []).map((m) => {
                                    const mine = m.sender_id === accountId;
                                    return (
                                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                            <div className={`max-w-[80%] rounded-sm px-3 py-1.5 text-sm ${mine ? "bg-[#111827] text-white" : "bg-gray-100 text-gray-800"}`}>
                                                <div className="text-[10px] opacity-70 mb-0.5">{m.sender_name} · {new Date(m.at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
                                                {m.text}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {["open", "accepted"].includes(deal.status) && role !== "viewer" && (
                                <div className="flex gap-2 mt-2">
                                    <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Nachricht schreiben…" onKeyDown={(e) => e.key === "Enter" && sendMessage()} data-testid="b2b-message-input" />
                                    <Button onClick={sendMessage} disabled={busy || !msg.trim()} data-testid="b2b-message-send"><Send className="w-4 h-4" /></Button>
                                </div>
                            )}
                        </div>

                        {/* Aktionen */}
                        {canManage && isOpen && !showReject && (
                            <div className="border-t border-gray-200 pt-3 space-y-3">
                                {deal.mode === "kauf" && (
                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid="b2b-accept-auto-toggle">
                                        <input type="checkbox" checked={autoMode} onChange={(e) => setAutoMode(e.target.checked)} />
                                        Vollautomatisch abschließen (Fahrzeug verkaufen + Rechnungsentwurf erstellen) — sonst Schritt für Schritt
                                    </label>
                                )}
                                <div className="flex flex-wrap gap-2">
                                    <Button onClick={accept} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="b2b-accept-btn">
                                        <Check className="w-4 h-4 mr-1" /> Annehmen & freigeben
                                    </Button>
                                    <Button variant="outline" onClick={() => setShowReject(true)} disabled={busy} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="b2b-reject-open-btn">
                                        <Ban className="w-4 h-4 mr-1" /> Ablehnen
                                    </Button>
                                    {deal.mode === "vermittlung" && deal.provision_counter_eur == null && (
                                        <Button variant="outline" onClick={() => setShowProvision((s) => !s)} disabled={busy} data-testid="b2b-provision-open-btn">
                                            <Euro className="w-4 h-4 mr-1" /> Provisions-Gegenvorschlag
                                        </Button>
                                    )}
                                </div>
                                {showProvision && (
                                    <div className="flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-sm p-2" data-testid="b2b-provision-box">
                                        <div className="flex-1">
                                            <Label className="text-xs">Neue Provision (€) — einmalig</Label>
                                            <Input type="number" min="0" value={provision} onChange={(e) => setProvision(e.target.value)} data-testid="b2b-provision-input" />
                                        </div>
                                        <Button onClick={sendProvision} disabled={busy} data-testid="b2b-provision-send">Senden</Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Ablehn-Formular */}
                        {canManage && isOpen && showReject && (
                            <div className="border-t border-gray-200 pt-3 space-y-2" data-testid="b2b-reject-form">
                                <Label className="text-xs">Ablehnungsgrund</Label>
                                <div className="flex flex-wrap gap-2">
                                    {REJECT_OPTS.map((r) => (
                                        <button key={r.code} type="button" onClick={() => setRejectCode(r.code)}
                                            className={`text-xs font-bold uppercase tracking-wider px-2 py-1.5 rounded-sm border ${rejectCode === r.code ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-white text-gray-600 border-gray-300"}`}
                                            data-testid={`b2b-reject-reason-${r.code}`}>{r.label}</button>
                                    ))}
                                </div>
                                <textarea value={rejectText} onChange={(e) => setRejectText(e.target.value)} rows={2} placeholder="Erläuterung (optional)" className="w-full border border-gray-300 rounded-sm px-2 py-1.5 text-sm" data-testid="b2b-reject-text" />
                                <p className="text-[11px] text-gray-500">Die Ablehnung wird zusätzlich an den System-Admin gemeldet.</p>
                                <div className="flex gap-2">
                                    <Button onClick={reject} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="b2b-reject-submit"><Ban className="w-4 h-4 mr-1" /> Ablehnung senden</Button>
                                    <Button variant="outline" onClick={() => setShowReject(false)} disabled={busy}>Abbrechen</Button>
                                </div>
                            </div>
                        )}

                        {/* Buyer: zurückziehen */}
                        {role === "buyer" && isOpen && (
                            <div className="border-t border-gray-200 pt-3">
                                <Button variant="outline" onClick={withdraw} disabled={busy} data-testid="b2b-withdraw-btn"><Undo2 className="w-4 h-4 mr-1" /> Anfrage zurückziehen</Button>
                            </div>
                        )}

                        {/* Vermittler: Verkaufsprozess im Namen des Besitzers (nach Annahme) */}
                        {role === "buyer" && deal.mode === "vermittlung" && deal.status === "accepted" && (
                            <div className="border-t border-gray-200 pt-3 space-y-2" data-testid="b2b-broker-checkout">
                                <div className="bg-blue-50 border border-blue-200 rounded-sm px-3 py-2 text-[12px] text-blue-900">
                                    Der Verkaufsprozess wird <strong>im Namen des Besitzers ({deal.to_dealer_name})</strong> durch dich (Vermittler) angestoßen.
                                    Alle Dokumente &amp; die Rechnung laufen im Branding des Besitzers. Du erfasst <strong>Kundendaten</strong> und bereitest den <strong>Kaufvertrag</strong> vor —
                                    Rechnung, Zahlung &amp; Zulassung sowie die verpflichtende <strong>Gesamtgeschäft-Freigabe</strong> übernimmt der Besitzer.
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <Button onClick={() => setBrokerStep("customer")} className="bg-[#111827] text-[#FFCC00] hover:bg-black" data-testid="b2b-broker-customer-btn">
                                        <User className="w-4 h-4 mr-1" /> Kundendaten erfassen
                                    </Button>
                                    <Button onClick={() => setBrokerStep("contract")} variant="outline" data-testid="b2b-broker-contract-btn">
                                        <Send className="w-4 h-4 mr-1" /> Kaufvertrag vorbereiten
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Seller: abschließen */}
                        {canManage && deal.status === "accepted" && (
                            <div className="border-t border-gray-200 pt-3">
                                <Button onClick={complete} disabled={busy} className="bg-[#111827] text-[#FFCC00] hover:bg-black" data-testid="b2b-complete-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Als abgeschlossen markieren</Button>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end pt-2">
                    <Button variant="outline" onClick={onClose}><X className="w-4 h-4 mr-1" /> Schließen</Button>
                </div>

                {/* Vermittler-Checkout-Modals (Kundendaten / Vertrag im Namen des Besitzers) */}
                {brokerStep === "customer" && deal && (
                    <CustomerDataModal
                        listingId={deal.listing_id}
                        onClose={() => { setBrokerStep(null); load(); }}
                        onSaved={() => { setBrokerStep(null); load(); toast.success("Kundendaten gespeichert (im Namen des Besitzers)"); }}
                    />
                )}
                {brokerStep === "contract" && deal && (
                    <ContractSignModal
                        listingId={deal.listing_id}
                        hasCustomer
                        onClose={() => { setBrokerStep(null); load(); }}
                        onSent={() => { setBrokerStep(null); load(); }}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}
